import type { ScenarioModelArtifact } from '../archive/scenarioArchiveBuilder';
import type {
  ModelReferenceInfo,
  ModelTransport,
} from './modelTransport';

/** Default pull-chunk size (8 MB) — matches the host cap and stays under 32 MB gRPC. */
export const MODEL_CHUNK_BYTES = 8 * 1024 * 1024;

/** Outcome of gathering the model artifact for the archive. */
export interface GatheredModel {
  /** Bundled copy for card B's builder; absent when reference-only. */
  model?: ScenarioModelArtifact;
  /** Always recorded — the stable model identity. */
  reference: ModelReferenceInfo;
  /** Surfaced to the UI when a requested copy could not be produced. */
  warning?: string;
}

export interface GatherModelOptions {
  /** Override the pull-chunk size (mainly for tests). */
  chunkBytes?: number;
}

/**
 * Pulls the staged model copy from the plugin and assembles a
 * {@link ScenarioModelArtifact} for the archive builder. Reference-only / model
 * excluded → no bytes are fetched and only the reference (+ any warning) is
 * returned. Linked models with no staged bytes are skipped (best-effort).
 */
export async function gatherModelArtifact(
  transport: ModelTransport,
  includeModel: boolean,
  options: GatherModelOptions = {},
): Promise<GatheredModel> {
  const plan = await transport.exportModel(includeModel);

  if (plan.mode !== 'copy' || !plan.host) {
    return { reference: plan.reference, warning: plan.warning || undefined };
  }

  const chunkBytes = options.chunkBytes && options.chunkBytes > 0 ? options.chunkBytes : MODEL_CHUNK_BYTES;

  const data = await pullFile(transport, plan.stagingToken, plan.host.archiveRef, chunkBytes);

  const linkedModels: NonNullable<ScenarioModelArtifact['linkedModels']> = [];
  for (const link of plan.linkedModels) {
    if (link.sizeBytes <= 0) {
      // Link was enumerated but not bundled (missing/inaccessible) — skip silently.
      continue;
    }
    const linkData = await pullFile(transport, plan.stagingToken, link.archiveRef, chunkBytes);
    linkedModels.push({ archiveRef: link.archiveRef, data: linkData });
  }

  const model: ScenarioModelArtifact = {
    archiveRef: plan.host.archiveRef,
    data,
    ...(linkedModels.length > 0 ? { linkedModels } : {}),
  };

  return { model, reference: plan.reference, warning: plan.warning || undefined };
}

/** Loops chunked reads until EOF, concatenating into a single buffer. */
async function pullFile(
  transport: ModelTransport,
  stagingToken: string,
  archiveRef: string,
  chunkBytes: number,
): Promise<Uint8Array> {
  const parts: Uint8Array[] = [];
  let offset = 0;
  let total = 0;

  // Bound the loop defensively: at most (totalSize / 1) iterations, but rely on
  // EOF. A guard against a misbehaving host that never reports EOF.
  for (;;) {
    const chunk = await transport.readChunk(stagingToken, archiveRef, offset, chunkBytes);
    if (chunk.data.length > 0) {
      parts.push(chunk.data);
      offset += chunk.data.length;
    }
    total = chunk.totalSize;
    if (chunk.eof || chunk.data.length === 0) {
      break;
    }
  }

  return concat(parts, total);
}

function concat(parts: Uint8Array[], expectedTotal: number): Uint8Array {
  const length = parts.reduce((sum, p) => sum + p.length, 0);
  const out = new Uint8Array(expectedTotal > 0 ? expectedTotal : length);
  let pos = 0;
  for (const part of parts) {
    out.set(part, pos);
    pos += part.length;
  }
  // If the host reported a larger total than we received, trim to actual bytes.
  return pos === out.length ? out : out.subarray(0, pos);
}
